import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from '@/components/ui';
import { areasService } from '../../../services/areasService';
import type { Vendor, VendorCreatePayload, VendorMatch } from '../../../services/vendorService';
import { VendorApiError, vendorService } from '../../../services/vendorService';
import { canOfferAddToMyArea } from '../utils/callerAreas';
import { validateVendorFields } from '../utils/vendorValidation';

type VendorFormData = VendorCreatePayload;

interface Props {
  show: boolean;
  onHide: () => void;
  vendor?: Vendor | null;
  onSave: (data: VendorFormData) => Promise<void>;
  callerAreas?: Array<{ id: number; name: string }>;
  canAddArea?: boolean;
  onAdoptVendor?: (vendor: Vendor) => void;
}

const EMPTY: VendorFormData = {
  name: '',
  legalName: '',
  contactEmail: '',
  phoneNumber: '',
  gstBusinessNumber: '',
  wcbAccountNumber: '',
  address: '',
  erpVendorId: '',
  comments: '',
  serviceAreaIds: [],
};

type FieldErrors = Partial<Record<keyof VendorFormData | 'duplicateOverrideReason', string>>;

/**
 * Add / Edit Vendor (M16, H7).
 *
 * Validation is the app's own, inline under each field (`noValidate` turns off the browser's
 * bubbles), and mirrors the server's rules so the same input is refused in both places.
 *
 * Duplicates: the server refuses a name or legal name that an existing vendor already has
 * (ignoring case and spacing), and refuses a NEAR match until a reason is given. Both come
 * back with the matching vendors, which are listed here; for a near match a reason box
 * appears and the save can be retried with it.
 */
const VendorModal: React.FC<Props> = ({
  show,
  onHide,
  vendor,
  onSave,
  callerAreas = [],
  canAddArea = false,
  onAdoptVendor,
}) => {
  const [form, setForm] = useState<VendorFormData>(EMPTY);
  const [serviceAreas, setServiceAreas] = useState<Array<{ id: number; name: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [matches, setMatches] = useState<Vendor[]>([]);
  const [duplicate, setDuplicate] = useState<{ exact: boolean; matches: VendorMatch[] } | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  useEffect(() => {
    areasService
      .getServiceAreas()
      .then((areas: any[]) => setServiceAreas(areas ?? []))
      .catch(() => setServiceAreas([]));
  }, []);

  useEffect(() => {
    if (!show) return;
    setError(null);
    setFieldErrors({});
    setMatches([]);
    setDuplicate(null);
    setOverrideReason('');
    setForm(
      vendor
        ? {
            name: vendor.name,
            legalName: vendor.legalName ?? '',
            contactEmail: vendor.contactEmail ?? '',
            phoneNumber: vendor.phoneNumber ?? '',
            gstBusinessNumber: vendor.gstBusinessNumber ?? '',
            wcbAccountNumber: vendor.wcbAccountNumber ?? '',
            address: vendor.address ?? '',
            erpVendorId: vendor.erpVendorId ?? '',
            comments: vendor.comments ?? '',
            serviceAreaIds: vendor.serviceAreas?.map((sa) => sa.serviceAreaId) ?? [],
          }
        : { ...EMPTY }
    );
  }, [show, vendor]);

  useEffect(() => {
    if (!show || vendor) {
      setMatches([]);
      return;
    }
    const q = form.name.trim();
    if (q.length < 2) {
      setMatches([]);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      vendorService
        .getVendorsPaginated({ search: q, limit: 5 })
        .then((res) => {
          if (!cancelled) setMatches(res.data ?? []);
        })
        .catch(() => {
          if (!cancelled) setMatches([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [show, vendor, form.name]);

  const set = <K extends keyof VendorFormData>(key: K, value: VendorFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    // A name change invalidates the duplicate verdict the server gave for the old name.
    if (key === 'name' || key === 'legalName') setDuplicate(null);
  };

  const toggleArea = (id: number) => {
    set(
      'serviceAreaIds',
      form.serviceAreaIds.includes(id) ? form.serviceAreaIds.filter((x) => x !== id) : [...form.serviceAreaIds, id]
    );
  };

  const validate = (): FieldErrors => {
    const errors: FieldErrors = { ...validateVendorFields(form, { requireLegalName: true }) };
    if (!form.name.trim()) errors.name = 'Vendor name is required.';
    if (form.serviceAreaIds.length === 0) {
      // Mirrors the server rule rather than letting the request 409: a vendor with no
      // service area would be invisible to everyone, including whoever just created it.
      errors.serviceAreaIds = 'Select at least one service area – a vendor with none would be visible to nobody.';
    }
    if (duplicate && !duplicate.exact && !overrideReason.trim()) {
      errors.duplicateOverrideReason = 'Give a reason to save a vendor with a similar name.';
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    if (Object.values(errors).some(Boolean)) {
      setFieldErrors(errors);
      setError(null);
      return;
    }

    const trim = (v?: string | null) => (v ?? '').trim();
    try {
      setSaving(true);
      setError(null);
      await onSave({
        ...form,
        name: trim(form.name),
        legalName: trim(form.legalName),
        contactEmail: trim(form.contactEmail) || null,
        phoneNumber: trim(form.phoneNumber) || null,
        gstBusinessNumber: trim(form.gstBusinessNumber) || null,
        wcbAccountNumber: trim(form.wcbAccountNumber) || null,
        address: trim(form.address) || null,
        erpVendorId: trim(form.erpVendorId) || null,
        comments: trim(form.comments) || null,
        duplicateOverrideReason: duplicate && !duplicate.exact ? overrideReason.trim() : undefined,
      });
    } catch (err) {
      if (err instanceof VendorApiError && err.fieldErrors) {
        setFieldErrors(err.fieldErrors as FieldErrors);
        setError(null);
      } else if (
        err instanceof VendorApiError &&
        (err.code === 'DUPLICATE_VENDOR' || err.code === 'POSSIBLE_DUPLICATE_VENDOR')
      ) {
        setDuplicate({ exact: err.code === 'DUPLICATE_VENDOR', matches: err.matches ?? [] });
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to save vendor');
      }
    } finally {
      setSaving(false);
    }
  };

  const textField = (
    key: keyof VendorFormData,
    label: string,
    options: { required?: boolean; type?: string; placeholder?: string; help?: string } = {}
  ) => (
    <Form.Group className="tw-mb-3" controlId={`vendor-${String(key)}`}>
      <Form.Label>
        {label}
        {options.required ? ' *' : ''}
      </Form.Label>
      <Form.Control
        type={options.type ?? 'text'}
        value={(form[key] as string | null | undefined) ?? ''}
        placeholder={options.placeholder}
        onChange={(e) => set(key, e.target.value as never)}
        isInvalid={!!fieldErrors[key]}
        aria-invalid={!!fieldErrors[key]}
        aria-describedby={fieldErrors[key] ? `vendor-${String(key)}-error` : undefined}
      />
      {fieldErrors[key] ? (
        <Form.Control.Feedback id={`vendor-${String(key)}-error`}>{fieldErrors[key]}</Form.Control.Feedback>
      ) : (
        options.help && <Form.Text className="tw-text-content-muted">{options.help}</Form.Text>
      )}
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={onHide} size="2xl">
      <Modal.Header closeButton>
        <Modal.Title>{vendor ? `Edit ${vendor.name}` : 'Add Vendor'}</Modal.Title>
      </Modal.Header>
      {/* The form sits between the panel and Modal.Body, so it must carry the flex-column
          contract itself. Without it Body grows to full content height and the footer is
          clipped by the panel's overflow. */}
      <Form onSubmit={handleSubmit} noValidate className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {!vendor && (
            <Alert variant="info">
              New vendors are created <strong>pending approval</strong>. Search first – if this
              subcontractor already exists in another service area, add it to yours instead of
              creating a duplicate. Resources can be added immediately; someone with Activate Vendor
              permission must still activate the vendor before it can be used, and activation is
              blocked until valid WCB and liability insurance documents are attached.
            </Alert>
          )}

          {duplicate && (
            <Alert variant={duplicate.exact ? 'danger' : 'warning'}>
              <div className="tw-mb-2 tw-font-medium">
                {/* V3-012: a legal-name collision says so. */}
                {duplicate.exact
                  ? duplicate.matches[0]?.matchedOn === 'legalName'
                    ? 'A vendor with this legal name already exists. Use the existing vendor instead of creating a duplicate.'
                    : 'A vendor with this name already exists. Use the existing vendor instead of creating a duplicate.'
                  : 'Vendors with a similar name already exist. If this is a different company, give a reason below and save again.'}
              </div>
              <ul className="tw-mb-0 tw-pl-4">
                {duplicate.matches.map((m) => (
                  <li key={m.id}>
                    <a href={`/vendors/${m.id}`} target="_blank" rel="noreferrer">
                      {m.name}
                    </a>
                    {m.legalName && m.legalName !== m.name && (
                      <span className="tw-text-content-muted"> ({m.legalName})</span>
                    )}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {!vendor && !duplicate && matches.length > 0 && (
            <Alert variant="warning">
              <div className="tw-font-medium tw-mb-2">Existing vendors with a similar name</div>
              <ul className="tw-mb-0 tw-pl-4">
                {matches.map((m) => {
                  const areas = m.serviceAreas?.map((sa) => sa.serviceArea.name).join(', ') || '–';
                  const canAdopt =
                    canAddArea && canOfferAddToMyArea(m, callerAreas.map((a) => a.id));
                  return (
                    <li key={m.id} className="tw-mb-1">
                      <span className="tw-font-medium">{m.name}</span>
                      <span className="tw-text-content-muted"> · {areas}</span>
                      {canAdopt && (
                        <Button
                          variant="link"
                          size="sm"
                          className="tw-p-0 tw-ml-2 tw-align-baseline"
                          type="button"
                          onClick={() => onAdoptVendor?.(m)}
                        >
                          Add to my area
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Alert>
          )}

          <Row>
            <Col md={6}>{textField('name', 'Vendor Name', { required: true })}</Col>
            <Col md={6}>{textField('legalName', 'Legal Name', { required: true })}</Col>
          </Row>

          {duplicate && !duplicate.exact && (
            <Form.Group className="tw-mb-3" controlId="vendor-duplicate-reason">
              <Form.Label>Reason this is not a duplicate *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={overrideReason}
                onChange={(e) => {
                  setOverrideReason(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, duplicateOverrideReason: undefined }));
                }}
                isInvalid={!!fieldErrors.duplicateOverrideReason}
                placeholder="e.g. Different company – same owner family, separate GST number"
              />
              {fieldErrors.duplicateOverrideReason ? (
                <Form.Control.Feedback>{fieldErrors.duplicateOverrideReason}</Form.Control.Feedback>
              ) : (
                <Form.Text className="tw-text-content-muted">
                  Saved with the vendor and shown to whoever activates it.
                </Form.Text>
              )}
            </Form.Group>
          )}

          <Row>
            <Col md={6}>
              {textField('contactEmail', 'Contact Email', { type: 'email', placeholder: 'name@company.ca' })}
            </Col>
            <Col md={6}>
              {textField('phoneNumber', 'Phone Number', { type: 'tel', placeholder: '250-555-0123' })}
            </Col>
          </Row>

          <Row>
            <Col md={4}>{textField('gstBusinessNumber', 'GST / Business Number', { placeholder: '123456789 RT0001' })}</Col>
            <Col md={4}>{textField('wcbAccountNumber', 'WCB Account Number')}</Col>
            <Col md={4}>
              {textField('erpVendorId', 'ERP Vendor ID', { help: 'The vendor’s id in the ERP, for matching invoices.' })}
            </Col>
          </Row>

          <Form.Group className="tw-mb-3" controlId="vendor-address">
            <Form.Label>Address</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.address ?? ''}
              onChange={(e) => set('address', e.target.value)}
            />
          </Form.Group>

          <Form.Group className="tw-mb-3">
            <Form.Label>Service Areas *</Form.Label>
            <div className="tw-text-sm tw-text-content-muted tw-mb-2">
              The areas this vendor is approved to operate in. A vendor can operate in several –
              resources and rates are then set per area.
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-3">
              {serviceAreas.map((sa) => (
                <Form.Check
                  key={sa.id}
                  type="checkbox"
                  id={`vendor-sa-${sa.id}`}
                  label={sa.name}
                  checked={form.serviceAreaIds.includes(sa.id)}
                  onChange={() => toggleArea(sa.id)}
                  isInvalid={!!fieldErrors.serviceAreaIds}
                />
              ))}
            </div>
            {fieldErrors.serviceAreaIds && (
              <Form.Control.Feedback className="tw-block">{fieldErrors.serviceAreaIds}</Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="tw-mb-3" controlId="vendor-comments">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.comments ?? ''}
              onChange={(e) => set('comments', e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" type="button" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving || (duplicate?.exact ?? false)}>
            {saving ? 'Saving…' : vendor ? 'Save Changes' : 'Create Vendor'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default VendorModal;
